/*** IMPORTS FROM imports-loader ***/
(function() {

define([
  'jquery',
  'pip-model',
  'pip-events',
  'pip-common.utils',
  'floor-calc',
  'configurable-calc',
  'calculator-manifest'
], function (
  $,
  pip,
  pipEvents,
  pipCommonUtils,
  FloorCalculator,
  Calculator,
  calculatorManifest
) {

  var BUYABLE_FLOOR_CALCULATOR = 'buyableFloorCalculator';
  var SCHEDULE_FLOOR_CALCULATOR = 'scheduleFloorCalculator';

  var view = {
    elements: {
      calculatorContainer: '#calculator-container',
    }
  };

  var viewModel = {
    shouldDisplayCalculator: function shouldDisplayCalculator() {
      return (viewModel.shouldDisplayLegacyCalculator() || viewModel.shouldDisplayConfigurableCalculator());
    },

    shouldDisplayLegacyCalculator: function shouldDisplayLegacyCalculator() {
      return !!(pip.projectCalculatorEligible
        && pip.features.projectCalculatorFeature.isFeatureEnabled
        && pip.products.primary.data
        && pip.products.primary.data.storeSkus
        && pip.products.primary.data.storeSkus[0]
        && pip.products.primary.data.storeSkus[0].pricing
        && pip.products.primary.data.storeSkus[0].pricing.alternatePricingDisplay)
    },

    shouldDisplayConfigurableCalculator: function shouldDisplayConfigurableCalculator() {
      return !!(pip.features.configurableCalcEnabled && pipCommonUtils.deepGet(pip.products.primary,'data.info.calculatorType'));
    },

    shouldApplyOverageMultiplier: function shouldApplyOverageMultiplier() {
      if (pip.features && pip.features.linkOverageToApiEnabled && pip.overagePercentage !== null) {
        return pip.overagePercentage > 0;
      }

      return pip.overageMultiplierEligible;
    },

    shouldDisplayQuantityCalculator: function shouldDisplayQuantityCalculator() {
      return !!(pip.fulfillment
        && pip.fulfillment.fulfillable
        && (!pip.installServices || !pip.installServices.scheduleAMeasure)
        && (viewModel.shouldDisplayFlooringCalculator() || viewModel.shouldDisplayConfigurableCalculator()));
    },

    shouldDisplayFlooringCalculator: function shouldDisplayFlooringCalculator() {
      return !!(parseFloat(pip.products.primary.data.storeSkus[0].pricing.unitsPerCase));
    },

    shouldDisplayScheduleCalculator: function shouldDisplayScheduleCalculator() {
      return pip.installServices
          && pip.installServices.scheduleAMeasure
          && !undefinedUnitSpecialPrice();
    },

    displayUOM: function displayUOM(uom) {
      var uomMap = {
        'each': '',
        'pallet': 'pallet'
      };

      var arr = Object.keys(uomMap).filter(function (uomKey) {
        return uom.indexOf(uomKey) > -1;
      });

      return arr.length ? uomMap[arr[0]] : uom.toLowerCase();
    },

    pluralizeUnitOfMeasure: function pluralizeUnitOfMeasure(uom) {
      if (!uom || uom === 'each') {
        return '';
      }
      // handle special cases, such as pallet(lift) here
      var uomMap = {
        'square yard': 'square yards',
        'case': 'cases',
        'pallet': 'pallets',
        'square foot': 'square feet'
      };
      var matchingUoms = Object.keys(uomMap).filter(function (uomKey) {
        return uom.indexOf(uomKey) > -1
      });
      if (matchingUoms.length) { // found a match, return the special case
        return uomMap[matchingUoms[0]];
      }

      // handle other cases
      if (uom.length > 1) {
        var lastChar = uom[uom.length - 1];
        var lastTwoChars = uom.substring(uom.length - 2);

        if ('sxz'.indexOf(lastChar.toLowerCase()) !== -1
          || ['sh', 'ch'].indexOf(lastTwoChars.toLowerCase()) !== -1) {

          return uom + 'es';
        }
      }

      return uom + 's';
    }
  };

  var ESTIMATED_LABOR_COST = 1.135;
  var ADDITIONAL_MATERIAL_COST = 0.89;

  var initialModelState = {
    overageMultiplier: 1,
    overagePercentage: 0,
    unitsPerCase: 0,
    specialPrice: 0,
    unitOfMeasure: '',
    caseUnitUom: '',
    unitSpecialPrice: 0,

    calculated: {
      squareFootage: 0,
      numberOfUnitsNeeded: 0,
      amountOfCoverage: 0,
      totalPrice: 0
    }
  };

  var model = {
    displayedUnitOfMeasure: '',

    flooringBuyable: {
      methods: {
        amountOfCoverage: function amountOfCoverage() {
          return model.calculated.numberOfUnitsNeeded * model.unitsPerCase;
        },
        totalPrice: function totalPrice() {
          return model.calculated.numberOfUnitsNeeded * model.specialPrice;
        }
      },
      updateCalculatedQuantityValues: function updateCalculatedQuantityValues(numberOfUnitsNeeded) {
        model.calculated.numberOfUnitsNeeded = numberOfUnitsNeeded;
        model.calculated.amountOfCoverage = model.flooringBuyable.methods.amountOfCoverage().toFixed(2);
        model.calculated.totalPrice = model.flooringBuyable.methods.totalPrice().toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      }
    },

    flooringNonBuyable: {
      methods: {
        getEstimatedTotal: function getEstimatedTotal() {
          var total = Math.ceil((model.calculated.squareFootage * ESTIMATED_LABOR_COST) * (model.unitSpecialPrice + ADDITIONAL_MATERIAL_COST));
          var estimatedTotal = (Math.ceil(total / 100) * 100);
          return parseInt(estimatedTotal, 10).toLocaleString();
        }
      }
    },

    updateModelWithProductData: function updateModelWithProductData() {
      model.unitOfMeasure = pip.products.primary.data.storeSkus[0].pricing.uom && pip.products.primary.data.storeSkus[0].pricing.uom.toLowerCase();
      model.caseUnitUom = (pip.products.primary.data.storeSkus[0].pricing.caseUnitUom && pip.products.primary.data.storeSkus[0].pricing.caseUnitUom.toLowerCase()) || 'unit';
      model.unitsPerCase = parseFloat(pip.products.primary.data.storeSkus[0].pricing.unitsPerCase) || 1;
      model.specialPrice = parseFloat(pip.products.primary.data.storeSkus[0].pricing.specialPrice);
      model.unitSpecialPrice = parseFloat(pip.products.primary.data.storeSkus[0].pricing.unitSpecialPrice);
      model.overagePercentage = model.getOveragePercentage();
      model.overageMultiplier = model.getOverageMultiplier();
    },

    getOveragePercentage: function overagePercentage() {
      if (pip.features && pip.features.linkOverageToApiEnabled && pip.overagePercentage !== null) {
        return pip.overagePercentage;
      }
      return 10;
    },

    getOverageMultiplier: function overageMultiplier() {
      if (viewModel.shouldApplyOverageMultiplier()) {
        if (pip.features && pip.features.linkOverageToApiEnabled && pip.overagePercentage !== null) {
          return 1 + (pip.overagePercentage / 100);
        }
        return 1.1;
      }
      return 1;
    }
  };

  var undefinedUnitSpecialPrice = function hasUnitSpecialPrice() {
    return pip.products.primary.data
        && pip.products.primary.data.storeSkus
        && pip.products.primary.data.storeSkus[0]
        && pip.products.primary.data.storeSkus[0].pricing
        && pip.products.primary.data.storeSkus[0].pricing.unitSpecialPrice === undefined;
  };

  var getCalculatorType = function initCalculatorType() {
    if (viewModel.shouldDisplayQuantityCalculator()) {
      if(viewModel.shouldDisplayFlooringCalculator()){
        return BUYABLE_FLOOR_CALCULATOR;
      }
    }

    if (viewModel.shouldDisplayScheduleCalculator()) {
      return SCHEDULE_FLOOR_CALCULATOR;
    }

    return 'unknownCalculatorType';
  };

  var initCalculatorForType = function initCalculatorForType(calculatorType) {
    if(viewModel.shouldDisplayQuantityCalculator()
      && pipCommonUtils.deepGet(pip.products.primary,'data.info.calculatorType')
      && pip.features.configurableCalcEnabled) {
        var calculatorConfig = calculatorManifest.getCalculatorConfig(pip.products.primary.data.info.calculatorType, pip, viewModel, model);
        if (calculatorConfig && calculatorConfig.coveragePerUnit) {
          return new Calculator.default(view.elements.calculatorContainer, calculatorManifest.getCalculatorConfig(pip.products.primary.data.info.calculatorType, pip, viewModel, model));
        }
  }

    if (calculatorType === BUYABLE_FLOOR_CALCULATOR) {
      return new FloorCalculator.default(view.elements.calculatorContainer, {
        uom: viewModel.displayUOM(model.unitOfMeasure),
        uomPlural: viewModel.pluralizeUnitOfMeasure(model.unitOfMeasure),
        dimension: model.caseUnitUom,
        pricePerUnit: model.specialPrice,
        areaPerUnit: model.unitsPerCase,
        displayOverage: viewModel.shouldApplyOverageMultiplier(),
        overage: model.overagePercentage,
        dimensionsEnabled: pip.features.floorCalcDimensionsEnabled
      });
    }

    if (calculatorType === SCHEDULE_FLOOR_CALCULATOR) {
      return new FloorCalculator.default(view.elements.calculatorContainer, {
        uom: viewModel.displayUOM(model.unitOfMeasure),
        uomPlural: viewModel.pluralizeUnitOfMeasure(model.unitOfMeasure),
        dimension: model.caseUnitUom,
        pricePerUnit: model.specialPrice,
        areaPerUnit: model.unitsPerCase,
        buyable: false,
        dimensionsEnabled: pip.features.floorCalcDimensionsEnabled
      });
    }

    return {};
  };

  var initEventListeners = function initEventListeners(calculatorType, calculator) {
    pipEvents.pipModel.pipModelQuantityUpdated.off('.quantityUpdated');
    pipEvents.pipModel.pipModelQuantityUpdated.on('.quantityUpdated', function () {
      model.flooringBuyable.updateCalculatedQuantityValues(pip.customer.quantity);
      if(pip.features.floorCalcDimensionsEnabled) {
        if(pip.features.configurableCalcEnabled) {
          calculator.setCustomQuantity(pip.customer.quantity);
        } else {
          calculator.update({ customQuantity: pip.customer.quantity })
        }
      } else {
        calculator.update({ calculatedQty: pip.customer.quantity });
      }
    });

    $(window.document).off('floor-calculated');
    $(window.document).on('floor-calculated', function (e) {
      model.calculated.squareFootage = e.detail.enteredArea;

      if (calculatorType === BUYABLE_FLOOR_CALCULATOR) {
        model.calculated.numberOfUnitsNeeded = pip.features.floorCalcDimensionsEnabled ? e.detail.quantity: e.detail.calculatedQty;
        model.calculated.amountOfCoverage = e.detail.calculatedArea;
        model.calculated.totalPrice = e.detail.calculatedPrice;
        model.displayedUnitOfMeasure = e.detail.calculatedUomLabel;
        pip.viewModel.calculator = $.extend({}, model);
        pipEvents.calculator.quantityCalculatorStateChanged.trigger();
      }

      if (calculatorType === SCHEDULE_FLOOR_CALCULATOR) {
        model.calculated.totalPrice = model.flooringNonBuyable.methods.getEstimatedTotal();
        pip.viewModel.calculator = $.extend({}, model);
        pipEvents.calculator.measureCalculatorStateChanged.trigger();
      }
    });

    $(window.document).off('configurable-calculated');
    $(window.document).on('configurable-calculated', function (e) {
      model.calculated.squareFootage = e.detail.enteredAmount;
      model.calculated.numberOfUnitsNeeded = e.detail.quantity;
      model.calculated.amountOfCoverage = e.detail.calculatedAmount;
      model.calculated.totalPrice = e.detail.calculatedPrice;
      model.displayedUnitOfMeasure = e.detail.calculatedUomLabel;
      pip.viewModel.calculator = $.extend({}, model);
      pipEvents.calculator.quantityCalculatorStateChanged.trigger();
    });
  };

  return {
    init: function () {
      if (viewModel.shouldDisplayCalculator()) {
        $.extend(true, model, initialModelState);
        model.updateModelWithProductData();

        var calculatorType = getCalculatorType();
        var calculator = initCalculatorForType(calculatorType);
        initEventListeners(calculatorType, calculator);
      }
    },
    view: view,
    viewModel: viewModel,
    model: model
  };
});

}.call(window));